import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

const STORAGE_KEY = "cookie-consent";

export default function CookieConsent() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = (choice: "accepted" | "rejected") => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, date: new Date().toISOString() })
    );
    setClosing(true);
    setTimeout(() => setVisible(false), 400);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookies.consent.title")}
      className={`fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[100] transition-all duration-400 ${
        closing
          ? "opacity-0 translate-y-6 scale-95"
          : "opacity-100 translate-y-0 scale-100 animate-in slide-in-from-bottom-8"
      }`}
    >
      <div className="relative rounded-2xl border border-border/60 bg-background/80 backdrop-blur-xl shadow-2xl p-5 md:p-6">
        <button
          onClick={() => dismiss("rejected")}
          aria-label={t("cookies.consent.close")}
          className="absolute top-3 right-3 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
        >
          <X className="size-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="shrink-0 mt-0.5 p-2 rounded-full bg-primary/10 text-primary">
            <Cookie className="size-5" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">
              {t("cookies.consent.title")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
              {t("cookies.consent.description")}{" "}
              <Link
                to="/legal/cookies"
                className="story-link text-foreground"
              >
                {t("cookies.consent.learnMore")}
              </Link>
              .
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dismiss("rejected")}
            className="sm:order-1"
          >
            {t("cookies.consent.reject")}
          </Button>
          <Button
            size="sm"
            onClick={() => dismiss("accepted")}
            className="sm:order-2"
          >
            {t("cookies.consent.accept")}
          </Button>
        </div>
      </div>
    </div>
  );
}
